import { IMilestone } from "@mctypes/global.types";
import { Box, Flex, Progress, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { formatDateYmdDashes } from "../../admin/selectors";
import WidgetWrapper from "../../../../components/WidgetWrapper/WidgetWrapper";
import { useAppSelector } from "../../../../hooks";

interface IProgress extends IMilestone {
  complete: boolean;
}
const TimelineWidget: React.FC = () => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const [progressArray, setProgressArray] = useState<IProgress[]>([]);
  const [completion, setCompletion] = useState(0);
  const timeLineRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  const num = (i) => {
    return (i / (progressArray.length - 1)) * 100;
  };

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        setWidth(entries[0].contentRect.width);
      }
    });
    if (timeLineRef.current) {
      observer.observe(timeLineRef.current);
    }
    return () => {
      if (timeLineRef.current) {
        observer.unobserve(timeLineRef.current);
      }
    };
  }, []);
  useEffect(() => {
    renderMilestones(project.milestones);
  }, [project.milestones]);

  useEffect(() => {
    if (progressArray.length > 0) {
      renderDate();
    }
  }, [progressArray]);

  const renderMilestones = (milestones) => {
    //places milestones in order of due date, and sets completed to true if due date is before today
    const copy = [...milestones];
    const dateNow = formatDateYmdDashes(new Date());
    const getTime = (date?: Date) => {
      return date != null ? new Date(date).getTime() : 0;
    };
    const sortedByDate = copy.sort((a, b) => getTime(a.dueDate) - getTime(b.dueDate));
    if (sortedByDate[0]?.dueDate > dateNow) {
      sortedByDate.unshift({
        dueDate: dateNow,
        name: "I dag",
      });
    }

    setProgressArray(
      sortedByDate.map((item) => {
        return {
          ...item,
          complete: item.dueDate <= dateNow,
        };
      }),
    );
  };

  const isUpperMilestoneLane = (index: number) => {
    return isTextOverflow && index % 2 !== 0;
  };

  const getMilestoneLabelTop = (index: number) => {
    return isUpperMilestoneLane(index) ? "-31px" : "18px";
  };

  const getMilestoneTickTop = (index: number) => {
    return isUpperMilestoneLane(index) ? "-6px" : "2px";
  };

  const MilestoneLabel = ({ item, index, isFirst, isLast }) => {
    return (
      <div
        style={{
          fontSize: `${isTextOverflow ? ".75em" : ".9em"}`,
          position: "absolute",
          top: getMilestoneLabelTop(index),
          lineHeight: "1.2em",
          paddingRight: "10px",
          left: isFirst || isLast ? "2px" : "12px",
          right: "0",
          width: `${isTextOverflow ? sectionWidth * 2 : sectionWidth}px`,
          maxWidth: `${isLast ? collapsedWidth : ""}px`,
        }}
      >
        <Flex direction="column" align="flex-start">
          <div className={`${isTextOverflow ? "text-truncate" : "two-line-text-truncate"}`} style={{ fontWeight: 500 }}>
            {item.name}
          </div>
          <Text c="#58595b" sz=".8em">
            {item.dueDate}
          </Text>
        </Flex>
      </div>
    );
  };

  const renderDate = () => {
    // renders the progress bar between milestones, milestones are set equal distance apart
    const dateNow = formatDateYmdDashes(new Date());
    let indexOfPrevious = progressArray.findLastIndex((item) => item.dueDate < dateNow);
    if (indexOfPrevious === -1) {
      indexOfPrevious = 0;
    }
    const PreviousDate = progressArray[indexOfPrevious];
    const $min = Date.parse(indexOfPrevious > -1 ? PreviousDate.dueDate : dateNow);
    const $max = Date.parse(
      progressArray[indexOfPrevious === progressArray.length - 1 ? indexOfPrevious : indexOfPrevious + 1].dueDate,
    );
    const $current = Date.parse(dateNow);
    const $percentage = Math.min(100, (100 - (($max + $current * -1) * 100) / ($max - $min)) / 100);
    return setCompletion(num(indexOfPrevious + $percentage));
  };
  const isTextOverflow = width < 125 * progressArray.length - 1;
  const sectionWidth = width / progressArray.length - 1;
  const collapsedWidth = sectionWidth < 130 ? sectionWidth : 130;
  const timelineWidth = width - collapsedWidth;
  return (
    <WidgetWrapper title="" widgetKey="timeline">
      <Box ref={timeLineRef} pos="absolute" style={{ top: 10, width: "100%" }}>
        {progressArray.length > 0 ? (
          <Box
            ml="0.5rem"
            pos="relative"
            style={{
              marginRight: "100px",
              marginTop: `${isTextOverflow ? "2.4em" : ".6em"}`,
              width: `${timelineWidth}px`,
            }}
          >
            <Flex w="100%" justify="space-between" pos="absolute" style={{ bottom: "-6px" }}>
              {progressArray.map((item, index) => (
                <div key={index} style={{ position: "relative" }}>
                  <MilestoneLabel
                    item={item}
                    index={index}
                    isFirst={index === 0}
                    isLast={index === progressArray.length - 1}
                  />
                  {index !== 0 && index !== progressArray.length - 1 && (
                    <div
                      style={{
                        width: "4px",
                        height: "16px",
                        borderRadius: "2px",
                        position: "relative",
                        top: getMilestoneTickTop(index),
                        backgroundColor: item.complete
                          ? "var(--mantine-color-green-filled)"
                          : "var(--mantine-color-gray-2)",
                      }}
                    />
                  )}
                </div>
              ))}
            </Flex>
            <Progress color="green" size={8} value={completion} style={{ width: "100%" }} />
          </Box>
        ) : (
          <div style={{ display: "grid", placeItems: "center", height: "75%" }}>
            <p>
              Ingen milepæler opprettet! Klikk her for å håndtere milepæler:{" "}
              <a href={`#/project/${project?._id}/settings/milestones`}>instillinger</a>
            </p>
          </div>
        )}
      </Box>
    </WidgetWrapper>
  );
};

export default TimelineWidget;
